import { AlertDialog, DialogTrigger } from '@react-spectrum/s2';
import Delete from '@react-spectrum/s2/icons/Delete';
import { useState } from 'react';

import { CloseIcon } from '@/components/icons/CloseIcon.tsx';

interface DetailPanelDeleteConfig {
	onConfirm: () => void | Promise<void>;
	confirmTitle: string;
	confirmMessage: string;
	ariaLabel?: string;
}

interface DetailPanelCardProps {
	title: string;
	onClose: () => void;
	onDelete?: DetailPanelDeleteConfig;
	style?: React.CSSProperties;
	children: React.ReactNode;
}

export const DetailPanelCard = ({
	title,
	onClose,
	onDelete,
	style,
	children,
}: DetailPanelCardProps) => {
	const [isDeleteOpen, setIsDeleteOpen] = useState(false);
	return (
		<div
			data-testid="detail-panel-card"
			className="glass-panel flex h-full min-h-0 min-w-0 flex-col
				overflow-hidden"
			style={style}
		>
			<div
				className="flex shrink-0 items-center justify-between gap-4 px-6 pt-4
					pb-6"
			>
				<h2
					className="min-w-0 flex-1 truncate text-xl font-bold
						text-(--text-primary)"
				>
					{title}
				</h2>
				<div className="flex items-center gap-1">
					{onDelete && (
						<>
							<button
								type="button"
								onClick={() => setIsDeleteOpen(true)}
								aria-label={onDelete.ariaLabel ?? 'Delete'}
								data-testid="detail-panel-card-delete"
								className="rounded-full p-1 text-gray-500 transition-colors
									[--iconPrimary:currentColor] hover:bg-gray-100
									hover:text-gray-700"
							>
								<Delete />
							</button>
							<DialogTrigger
								isOpen={isDeleteOpen}
								onOpenChange={setIsDeleteOpen}
							>
								<span hidden />
								<AlertDialog
									variant="destructive"
									title={onDelete.confirmTitle}
									primaryActionLabel="Delete"
									cancelLabel="Cancel"
									onPrimaryAction={() => {
										void onDelete.onConfirm();
									}}
								>
									{onDelete.confirmMessage}
								</AlertDialog>
							</DialogTrigger>
						</>
					)}
					<button
						type="button"
						onClick={onClose}
						aria-label="Close panel"
						data-testid="detail-panel-card-close"
						className="rounded-full p-1 text-gray-500 transition-colors
							hover:bg-gray-100 hover:text-gray-700"
					>
						<CloseIcon className="size-5" />
					</button>
				</div>
			</div>
			<div className="flex min-h-0 min-w-0 flex-1 flex-col px-6 pb-6">
				<div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto">
					{children}
				</div>
			</div>
		</div>
	);
};
